import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { usePermissions, RESOURCES } from '@/contexts/PermissionsContext';
import { ImpersonationBanner } from './ImpersonationBanner';
import { NotificationBell } from './NotificationBell';
import { Menu, X, Plus, LayoutDashboard, LayoutGrid, List, FileText, Target, Settings, BarChart2, Users, Phone, XCircle, Building, ChevronDown, ChevronUp, Zap } from 'lucide-react';

// Navigation items in the requested order:
// 1. Add New Lead, 2. Dashboard, 3. Kanban Board, 4. All Leads, 5. Reports, 6. Templates, 7. Lead Sources, 8. Settings, 9. Meta Ads
const navItems = [
  { id: 'add', label: 'Add New Lead', path: '/leads/add', icon: Plus, requiresCreate: true, highlight: true },
  { id: 'dashboard', label: 'Dashboard', path: '/leads', icon: LayoutDashboard, requiresCreate: false },
  { id: 'kanban', label: 'Kanban Board', path: '/leads/kanban', icon: LayoutGrid, requiresCreate: false },
  { id: 'list', label: 'All Leads', path: '/leads/list', icon: List, requiresCreate: false },
  // Reports will be inserted here as a dropdown
  { id: 'templates', label: 'Templates', path: '/leads/settings/templates', icon: FileText, requiresCreate: false },
  { id: 'sources', label: 'Lead Sources', path: '/leads/settings/sources', icon: Target, requiresCreate: false },
  { id: 'meta-ads', label: 'Meta Lead Ads', path: '/leads/settings/meta-ads', icon: Zap, requiresCreate: false },
  { id: 'settings', label: 'Settings', path: '/leads/settings/auto-followup', icon: Settings, requiresCreate: false },
];

const reportsNavItems = [
  { id: 'source-report', label: 'Lead Source Analysis', path: '/leads/reports/source', icon: BarChart2 },
  { id: 'rep-report', label: 'Sales Rep Performance', path: '/leads/reports/rep-performance', icon: Users },
  { id: 'follow-up', label: 'Follow-up Due', path: '/leads/reports/follow-up', icon: Phone },
  { id: 'lost-report', label: 'Lost Lead Analysis', path: '/leads/reports/lost', icon: XCircle },
  { id: 'financiers-report', label: 'Financiers Report', path: '/leads/reports/financiers', icon: Building },
];

export function LeadsLayout({ children, user, onLogout, title }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [reportsOpen, setReportsOpen] = React.useState(false);
  const { canCreate } = usePermissions();

  const isActive = (path) => {
    if (path === '/leads') return location.pathname === '/leads';
    return location.pathname.startsWith(path);
  };

  // Filter nav items based on permissions
  const filteredNavItems = navItems.filter(item => {
    if (item.requiresCreate) {
      return canCreate(RESOURCES.LEADS);
    }
    return true;
  });

  // Close menu when navigating
  const handleNavigation = (path) => {
    navigate(path);
    setMenuOpen(false);
    setReportsOpen(false);
  };

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuOpen && !e.target.closest('.menu-panel') && !e.target.closest('.menu-toggle')) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen]);

  return (
    <div className="min-h-screen page-wash-leads">
      <ImpersonationBanner />
      <Toaster position="top-right" richColors />

      {/* Header — premium frosted glass with emerald accent (Phase 3) */}
      <header className="bg-white/70 backdrop-blur-xl shadow-[0_1px_2px_rgba(0,0,0,0.04)] border-b border-emerald-100/70 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Hamburger Menu Button - visible on all screen sizes */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="menu-toggle p-3 hover:bg-emerald-50 rounded-xl transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95 flex items-center justify-center min-w-[44px] min-h-[44px]"
                aria-label="Toggle menu"
                data-testid="menu-toggle"
              >
                {menuOpen ? (
                  <X className="w-6 h-6 text-emerald-700 pointer-events-none" />
                ) : (
                  <Menu className="w-6 h-6 text-emerald-700 pointer-events-none" />
                )}
              </button>

              <div>
                <h1 className="text-lg md:text-xl font-semibold text-slate-900 flex items-center gap-2 tracking-tight" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 shadow-[0_4px_12px_rgba(5,150,105,0.25)]">
                    <Target className="w-4 h-4 text-white pointer-events-none" />
                  </span>
                  <span className="hidden sm:inline">Lead Management</span>
                  <span className="sm:hidden">Leads</span>
                </h1>
                <p className="text-[11px] text-slate-500 hidden sm:block tracking-wide font-medium mt-0.5">{title || 'Manage your leads efficiently'}</p>
              </div>
            </div>

            {/* User Menu & Quick Add */}
            <div className="flex items-center gap-2">
              {/* Notification Bell */}
              <NotificationBell />

              {/* Quick Add Button */}
              {canCreate(RESOURCES.LEADS) && (
                <button
                  onClick={() => navigate('/leads/add')}
                  className="p-2 text-white rounded-full shadow-[0_8px_20px_rgba(5,150,105,0.35)] hover:shadow-[0_10px_28px_rgba(5,150,105,0.50)] transition-all duration-180 ease-out hover:scale-[1.05] active:scale-95 min-w-[40px] min-h-[40px] flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 60%, #0891B2 100%)' }}
                  title="Quick Add Lead"
                  data-testid="quick-add-lead"
                >
                  <Plus className="w-5 h-5 pointer-events-none" />
                </button>
              )}

              <span className="text-sm text-slate-600 hidden lg:block font-medium">{user?.email}</span>
              <button
                onClick={onLogout}
                className="px-3 py-1.5 text-sm bg-rose-50 text-rose-700 rounded-xl border border-rose-100 hover:bg-rose-100 hover:border-rose-200 transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95 hidden sm:block font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Menu Overlay */}
      {menuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* Slide-in Menu Panel - Same for Mobile and Desktop */}
      <div 
        className={`menu-panel fixed top-0 left-0 h-full w-80 max-w-[85vw] bg-white/95 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.4)] z-50 transform transition-transform duration-300 ease-in-out ${
          menuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Menu Header */}
        <div className="text-white p-5" style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 50%, #0891B2 100%)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="w-6 h-6 pointer-events-none" />
              <span className="font-semibold text-lg tracking-tight" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>Lead Management</span>
            </div>
            <button 
              onClick={() => setMenuOpen(false)}
              className="p-2 hover:bg-white/20 rounded-xl transition-all duration-180 ease-out min-w-[40px] min-h-[40px] flex items-center justify-center"
            >
              <X className="w-5 h-5 pointer-events-none" />
            </button>
          </div>
          <p className="text-emerald-100 text-[12.5px] mt-1.5 font-medium tracking-wide">{user?.email}</p>
        </div>

        {/* Navigation Items */}
        <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          <nav className="space-y-1">
            {/* 1. Add New Lead */}
            {canCreate(RESOURCES.LEADS) && (
              <button
                onClick={() => handleNavigation('/leads/add')}
                className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all duration-240 ease-out ${
                  isActive('/leads/add')
                    ? 'text-white shadow-[0_8px_20px_rgba(5,150,105,0.30)]'
                    : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                }`}
                style={isActive('/leads/add') ? { background: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)' } : {}}
                data-testid="menu-add-lead"
              >
                <div className={`p-2 rounded-lg ${isActive('/leads/add') ? 'bg-white/20' : 'bg-emerald-100'}`}>
                  <Plus className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <div className="font-semibold tracking-tight">Add New Lead</div>
                  <div className={`text-[11px] ${isActive('/leads/add') ? 'text-emerald-50' : 'text-emerald-600'} font-medium`}>Quick data entry</div>
                </div>
              </button>
            )}

            {/* 2. Dashboard */}
            <button
              onClick={() => handleNavigation('/leads')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                location.pathname === '/leads'
                  ? 'bg-emerald-50 text-emerald-700 font-medium'
                  : 'text-gray-700 hover:bg-slate-100/80'
              }`}
            >
              <LayoutDashboard className={`w-5 h-5 ${location.pathname === '/leads' ? 'text-emerald-600' : 'text-gray-500'}`} />
              <span>Dashboard</span>
              {location.pathname === '/leads' && <div className="ml-auto w-2 h-2 bg-emerald-600 rounded-full" />}
            </button>

            {/* 3. Kanban Board */}
            <button
              onClick={() => handleNavigation('/leads/kanban')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                isActive('/leads/kanban')
                  ? 'bg-emerald-50 text-emerald-700 font-medium'
                  : 'text-gray-700 hover:bg-slate-100/80'
              }`}
            >
              <LayoutGrid className={`w-5 h-5 ${isActive('/leads/kanban') ? 'text-emerald-600' : 'text-gray-500'}`} />
              <span>Kanban Board</span>
              {isActive('/leads/kanban') && <div className="ml-auto w-2 h-2 bg-emerald-600 rounded-full" />}
            </button>

            {/* 4. All Leads */}
            <button
              onClick={() => handleNavigation('/leads/list')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                isActive('/leads/list')
                  ? 'bg-emerald-50 text-emerald-700 font-medium'
                  : 'text-gray-700 hover:bg-slate-100/80'
              }`}
            >
              <List className={`w-5 h-5 ${isActive('/leads/list') ? 'text-emerald-600' : 'text-gray-500'}`} />
              <span>All Leads</span>
              {isActive('/leads/list') && <div className="ml-auto w-2 h-2 bg-emerald-600 rounded-full" />}
            </button>

            {/* 4. Reports (Collapsible) */}
            <div>
              <button
                onClick={() => setReportsOpen(!reportsOpen)}
                className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                  location.pathname.includes('/leads/reports')
                    ? 'bg-emerald-50 text-emerald-700 font-medium'
                    : 'text-gray-700 hover:bg-slate-100/80'
                }`}
              >
                <div className="flex items-center gap-3">
                  <BarChart2 className={`w-5 h-5 ${location.pathname.includes('/leads/reports') ? 'text-emerald-600' : 'text-gray-500'}`} />
                  <span>Reports</span>
                </div>
                {reportsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              
              {reportsOpen && (
                <div className="ml-4 mt-2 space-y-1 border-l-2 border-emerald-200 pl-4">
                  {reportsNavItems.map(item => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleNavigation(item.path)}
                        className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition-all text-sm ${
                          location.pathname === item.path
                            ? 'bg-emerald-50 text-emerald-700 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Icon className={`w-4 h-4 ${location.pathname === item.path ? 'text-emerald-600' : 'text-gray-400'}`} />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 5. Templates */}
            <button
              onClick={() => handleNavigation('/leads/settings/templates')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                isActive('/leads/settings/templates')
                  ? 'bg-emerald-50 text-emerald-700 font-medium'
                  : 'text-gray-700 hover:bg-slate-100/80'
              }`}
            >
              <FileText className={`w-5 h-5 ${isActive('/leads/settings/templates') ? 'text-emerald-600' : 'text-gray-500'}`} />
              <span>Templates</span>
              {isActive('/leads/settings/templates') && <div className="ml-auto w-2 h-2 bg-emerald-600 rounded-full" />}
            </button>

            {/* 6. Lead Sources */}
            <button
              onClick={() => handleNavigation('/leads/settings/sources')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                isActive('/leads/settings/sources')
                  ? 'bg-emerald-50 text-emerald-700 font-medium'
                  : 'text-gray-700 hover:bg-slate-100/80'
              }`}
            >
              <Target className={`w-5 h-5 ${isActive('/leads/settings/sources') ? 'text-emerald-600' : 'text-gray-500'}`} />
              <span>Lead Sources</span>
              {isActive('/leads/settings/sources') && <div className="ml-auto w-2 h-2 bg-emerald-600 rounded-full" />}
            </button>

            {/* 7. Meta Lead Ads */}
            <button
              onClick={() => handleNavigation('/leads/settings/meta-ads')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                isActive('/leads/settings/meta-ads')
                  ? 'bg-emerald-50 text-emerald-700 font-medium'
                  : 'text-gray-700 hover:bg-slate-100/80'
              }`}
              data-testid="menu-meta-ads"
            >
              <Zap className={`w-5 h-5 ${isActive('/leads/settings/meta-ads') ? 'text-emerald-600' : 'text-gray-500'}`} />
              <span>Meta Lead Ads</span>
              {isActive('/leads/settings/meta-ads') && <div className="ml-auto w-2 h-2 bg-emerald-600 rounded-full" />}
            </button>

            {/* 8. Settings */}
            <button
              onClick={() => handleNavigation('/leads/settings/auto-followup')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                isActive('/leads/settings/auto-followup')
                  ? 'bg-emerald-50 text-emerald-700 font-medium'
                  : 'text-gray-700 hover:bg-slate-100/80'
              }`}
            >
              <Settings className={`w-5 h-5 ${isActive('/leads/settings/auto-followup') ? 'text-emerald-600' : 'text-gray-500'}`} />
              <span>Settings</span>
              {isActive('/leads/settings/auto-followup') && <div className="ml-auto w-2 h-2 bg-emerald-600 rounded-full" />}
            </button>
          </nav>
        </div>

        {/* Menu Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200/80 bg-slate-50/70 backdrop-blur-sm">
          <button
            onClick={() => handleNavigation('/modules')}
            className="w-full flex items-center justify-center gap-2 p-2 text-slate-600 hover:bg-slate-200/70 rounded-xl transition-all duration-180 ease-out font-medium mb-2"
          >
            ← Back to Modules
          </button>
          <button
            onClick={onLogout}
            className="w-full p-2 bg-rose-50 text-rose-700 rounded-xl border border-rose-100 hover:bg-rose-100 transition-all duration-180 ease-out font-medium"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Mobile Bottom Quick Actions Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/85 backdrop-blur-xl border-t border-emerald-100/60 shadow-[0_-4px_16px_rgba(0,0,0,0.04)] z-30">
        <div className="flex items-center justify-around p-2">
          {/* Add Lead - Center prominent button */}
          {canCreate(RESOURCES.LEADS) && (
            <button
              onClick={() => navigate('/leads/add')}
              className="flex flex-col items-center p-2 -mt-4 text-white rounded-full shadow-[0_8px_20px_rgba(5,150,105,0.40)] w-14 h-14 justify-center transition-transform duration-180 ease-out active:scale-95"
              style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 60%, #0891B2 100%)' }}
              data-testid="bottom-add-lead-btn"
            >
              <Plus className="w-6 h-6 pointer-events-none" />
            </button>
          )}
          
          <button
            onClick={() => navigate('/leads')}
            className={`flex flex-col items-center p-2 rounded-lg transition-colors min-w-[60px] ${
              location.pathname === '/leads' ? 'text-emerald-600' : 'text-gray-500'
            }`}
          >
            <LayoutDashboard className="w-5 h-5 pointer-events-none" />
            <span className="text-xs mt-1 pointer-events-none">Dashboard</span>
          </button>
          
          <button
            onClick={() => navigate('/leads/list')}
            className={`flex flex-col items-center p-2 rounded-lg transition-colors min-w-[60px] ${
              location.pathname.includes('/list') ? 'text-emerald-600' : 'text-gray-500'
            }`}
          >
            <List className="w-5 h-5 pointer-events-none" />
            <span className="text-xs mt-1 pointer-events-none">All Leads</span>
          </button>
          
          <button
            onClick={() => setMenuOpen(true)}
            className="flex flex-col items-center p-2 rounded-lg text-gray-500 min-w-[60px]"
          >
            <Menu className="w-5 h-5 pointer-events-none" />
            <span className="text-xs mt-1 pointer-events-none">More</span>
          </button>
        </div>
      </div>

      {/* Main Content - add padding for mobile bottom bar */}
      <main className="max-w-7xl mx-auto px-4 py-6 pb-24 md:pb-6 anim-fade-in">
        <div className="anim-slide-up">{children}</div>
      </main>
    </div>
  );
}
